import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { ArrowBack, CheckCircleOutline, Refresh } from "@mui/icons-material";
import HelmetMeta from "../../components/HelmetMeta";
import { AppContext } from "../../AppContext";
import { useGetDocTypeListWithChildren } from "../../shared/hooks/useDocTypesData";
import { fToNow } from "../../utils/formatTime";
import { useGetDocTypeList } from "../../shared/hooks/useFrappeDoctype";
import { useSetOrderReadyTime } from "../../shared/hooks/useTableOrdersData";
import { useParams } from "react-router-dom";
import LogoutButton from "../../components/LogoutButton";

function parseDateSafe(value) {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    const normalized =
        typeof value === "string" ? value.replace(" ", "T") : value;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
}

function getOrderCreatedAt(order) {
    return parseDateSafe(order?.creation || order?.creation_time);
}

function getItemsQtyTotal(items) {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, item) => sum + Number(item?.qty || 0), 0);
}

function getWaiterLabel(owner, usersByName) {
    if (!owner) return null;
    const fullName = usersByName?.get?.(owner);
    return `Waiter: ${fullName || owner}`;
}

function getOrderLastFoodReadyAt(historyDoc) {
    if (!historyDoc) return null;
    const rawOps = historyDoc?.operations;
    let ops = rawOps;
    if (typeof rawOps === "string") {
        try {
            ops = JSON.parse(rawOps);
        } catch (e) {
            ops = null;
        }
    }
    if (!Array.isArray(ops) || ops.length === 0) {
        return null;
    }
    let last = null;
    for (const op of ops) {
        if (!op || op.type !== "food_ready") continue;
        const t = parseDateSafe(op.time);
        if (!t) continue;
        if (!last || t.getTime() > last.getTime()) {
            last = t;
        }
    }
    return last;
}

function isOrderLate({ order, lastFoodReadyAt, thresholdMinutes, enabled }) {
    if (!enabled) return false;
    if (!thresholdMinutes || thresholdMinutes <= 0) return false;
    if (!order || order.food_ready_time) return false;

    const thresholdMs = thresholdMinutes * 60_000;
    const now = Date.now();
    const createdAt = getOrderCreatedAt(order);
    const ref = lastFoodReadyAt || createdAt;
    if (!ref) return false;
    return now - ref.getTime() >= thresholdMs;
}

function getItemNotes(item) {
    const notes = item?.notes ?? item?.item_notes ?? item?.note;
    if (!notes) return "";
    return String(notes).trim();
}

function OrderItemsList({ items }) {
    const safeItems = Array.isArray(items) ? items : [];
    if (safeItems.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No items.
            </Typography>
        );
    }
    return (
        <Stack spacing={1}>
            {safeItems.map((it) => {
                const key =
                    it?.name ||
                    it?.identifier ||
                    `${it?.item_code || it?.item_name}-${it?.idx || ""}`;
                const notes = getItemNotes(it);
                return (
                    <Box
                        key={key}
                        sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "flex-start",
                        }}
                    >
                        <Box sx={{ minWidth: 36 }}>
                            <Chip
                                size="small"
                                label={`x${it?.qty ?? 0}`}
                                sx={{ fontWeight: 700 }}
                                color="default"
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, lineHeight: 1.2 }}
                            >
                                {it?.item_name || it?.item_code || "Item"}
                            </Typography>
                            {notes ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {notes}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
}

function KitchenDashboard() {
    const { socket, foodLateTimeEnabled, foodLateTime } =
        useContext(AppContext);

    const { productionCenter } = useParams();

    const { data: productionCenterData, isLoading: isProductionCenterLoading } =
        useGetDocTypeListWithChildren({
            doctype: productionCenter ? "Restaurant Object" : null,
            filters: productionCenter
                ? [
                      ["description", "=", productionCenter],
                      ["type", "=", "Production Center"],
                  ]
                : null,
        });

    const productionCenterDoc = useMemo(() => {
        const list = Array.isArray(productionCenterData)
            ? productionCenterData
            : [];
        return list[0] || null;
    }, [productionCenterData]);

    const allowedItemGroups = useMemo(() => {
        const set = new Set();
        const groups = productionCenterDoc?.production_center_group || [];
        if (!Array.isArray(groups)) return set;
        groups.forEach((g) => {
            const ig = (g?.item_group || "").trim();
            if (ig) set.add(ig);
        });
        return set;
    }, [productionCenterDoc]);

    const filterItemsForCenter = (items) => {
        const safe = Array.isArray(items) ? items : [];
        if (!productionCenterDoc) return safe;
        if (allowedItemGroups.size === 0) return [];
        return safe.filter((it) => {
            const ig = (it?.item_group || "").trim();
            return ig && allowedItemGroups.has(ig);
        });
    };

    const { data: users } = useGetDocTypeList({
        doctype: "User",
        fields: ["name", "full_name"],
    });

    const {
        data: orders,
        isLoading,
        refetch,
        isFetching,
    } = useGetDocTypeListWithChildren({
        doctype: "Table Order",
        filters: [["status", "=", "Attending"]],
        order_by: "creation asc",
    });

    const normalizedOrders = useMemo(
        () => (Array.isArray(orders) ? orders : []),
        [orders]
    );
    const orderNames = useMemo(
        () => normalizedOrders.map((o) => o?.name).filter(Boolean),
        [normalizedOrders]
    );

    const { data: orderHistory } = useGetDocTypeList({
        doctype: orderNames.length > 0 ? "Order History" : null,
        filters: orderNames.length > 0 ? [["name", "in", orderNames]] : null,
    });

    const markReadyMutation = useSetOrderReadyTime();

    const [detailOrderName, setDetailOrderName] = useState(null);

    const usersByName = useMemo(() => {
        const list = Array.isArray(users) ? users : [];
        const m = new Map();
        list.forEach((u) => {
            if (!u?.name) return;
            const label = (u?.full_name || "").trim();
            m.set(u.name, label);
        });
        return m;
    }, [users]);

    // realtime updates (same pattern as ProductionCenter)
    useEffect(() => {
        if (!socket) return;
        socket.emit("doctype_subscribe", "Table Order");
        const handler = (data) => {
            if (data?.doctype === "Table Order") {
                refetch?.();
            }
        };
        socket.on("list_update", handler);
        return () => {
            if (socket?.off) {
                socket.off("list_update", handler);
            } else if (socket?.removeListener) {
                socket.removeListener("list_update", handler);
            }
        };
    }, [socket, refetch]);

    // lightweight polling fallback (kitchen screen should stay fresh even without socket)
    useEffect(() => {
        const id = setInterval(() => {
            refetch?.();
        }, 10 * 1000);
        return () => clearInterval(id);
    }, [refetch]);

    const thresholdMinutes = Number(foodLateTime);

    const lastFoodReadyAtByOrderName = useMemo(() => {
        const list = Array.isArray(orderHistory) ? orderHistory : [];
        const m = new Map();
        list.forEach((h) => {
            if (!h?.name) return;
            m.set(h.name, getOrderLastFoodReadyAt(h));
        });
        return m;
    }, [orderHistory]);

    const pendingOrders = useMemo(() => {
        const base = normalizedOrders
            .filter((o) => o && !o.food_ready_time)
            .sort((a, b) => {
                const da = getOrderCreatedAt(a)?.getTime?.() ?? 0;
                const db = getOrderCreatedAt(b)?.getTime?.() ?? 0;
                return da - db;
            });
        if (!productionCenterDoc) {
            return base;
        }
        return base
            .map((o) => ({
                ...o,
                __filteredEntryItems: filterItemsForCenter(o?.entry_items),
            }))
            .filter((o) => (o?.__filteredEntryItems || []).length > 0);
    }, [normalizedOrders, productionCenterDoc, allowedItemGroups]);

    const selectedOrder = useMemo(() => {
        if (!detailOrderName) return null;
        return pendingOrders.find((o) => o?.name === detailOrderName) || null;
    }, [pendingOrders, detailOrderName]);

    // Ensure selection stays valid when orders refresh
    useEffect(() => {
        if (!detailOrderName) return;
        const exists = pendingOrders.some((o) => o?.name === detailOrderName);
        if (!exists) setDetailOrderName(null);
    }, [pendingOrders, detailOrderName]);

    const handleMarkReady = (order) => {
        if (!order?.name || markReadyMutation.isLoading) return;
        markReadyMutation.mutate(
            {
                order,
            },
            {
                onSuccess: () => {
                    // keep UX snappy: refresh so it moves to the "Ready" list
                    refetch?.();
                    setDetailOrderName((prev) =>
                        prev === order.name ? null : prev
                    );
                },
            }
        );
    };

    const handleCloseDetails = () => setDetailOrderName(null);

    return (
        <>
            <HelmetMeta title="Kitchen" />
            <Box
                sx={{
                    p: 2,
                    height: "calc(100vh - 0px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    bgcolor: "background.default",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 900, lineHeight: 1.1 }}
                        >
                            Kitchen Dashboard
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pending orders (not ready): {pendingOrders.length}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <LogoutButton />
                        <IconButton
                            onClick={() => refetch?.()}
                            disabled={isFetching}
                        >
                            <Refresh />
                        </IconButton>
                    </Stack>
                </Box>

                <Divider />

                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {isProductionCenterLoading ? (
                        <Card
                            elevation={0}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                p: 3,
                                maxWidth: 720,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                Loading production center…
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                            >
                                Please wait.
                            </Typography>
                        </Card>
                    ) : !productionCenterDoc ? (
                        <Card
                            elevation={0}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                p: 3,
                                maxWidth: 720,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                No production center with the name {"{"}
                                {productionCenter || ""}
                                {"}"}
                            </Typography>
                        </Card>
                    ) : selectedOrder ? (
                        (() => {
                            const selectedLate = isOrderLate({
                                order: selectedOrder,
                                lastFoodReadyAt: lastFoodReadyAtByOrderName.get(
                                    selectedOrder?.name
                                ),
                                thresholdMinutes,
                                enabled: foodLateTimeEnabled,
                            });
                            return (
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: "2px solid",
                                        borderColor: selectedLate
                                            ? "error.main"
                                            : "primary.main",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        bgcolor: selectedLate
                                            ? "error.50"
                                            : undefined,
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Stack spacing={1.25}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    justifyContent:
                                                        "space-between",
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        minWidth: 0,
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-start",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={
                                                            handleCloseDetails
                                                        }
                                                        sx={{ mt: 0.25 }}
                                                        aria-label="Back to orders"
                                                    >
                                                        <ArrowBack />
                                                    </IconButton>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            fontWeight: 900,
                                                            lineHeight: 1.1,
                                                        }}
                                                    >
                                                        {selectedOrder?.table_description ||
                                                            "Order"}
                                                    </Typography>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display:
                                                                    "block",
                                                            }}
                                                        >
                                                            {
                                                                selectedOrder?.name
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    size="small"
                                                    color={
                                                        selectedLate
                                                            ? "error"
                                                            : "warning"
                                                    }
                                                    label={
                                                        selectedLate
                                                            ? "Late"
                                                            : "Pending"
                                                    }
                                                />
                                            </Box>

                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <Chip
                                                    size="small"
                                                    label={`Items: ${getItemsQtyTotal(
                                                        selectedOrder?.__filteredEntryItems ||
                                                            selectedOrder?.entry_items
                                                    )}`}
                                                />
                                                {selectedOrder?.owner ? (
                                                    <Chip
                                                        size="small"
                                                        label={getWaiterLabel(
                                                            selectedOrder.owner,
                                                            usersByName
                                                        )}
                                                    />
                                                ) : null}
                                                {(() => {
                                                    const createdAt =
                                                        getOrderCreatedAt(
                                                            selectedOrder
                                                        );
                                                    return createdAt ? (
                                                        <Chip
                                                            size="small"
                                                            label={`Created ${fToNow(
                                                                createdAt
                                                            )}`}
                                                        />
                                                    ) : null;
                                                })()}
                                            </Stack>

                                            <Divider />

                                            <Box>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 900,
                                                        mb: 1,
                                                    }}
                                                >
                                                    Entries
                                                </Typography>
                                                <OrderItemsList
                                                    items={
                                                        selectedOrder?.__filteredEntryItems ||
                                                        selectedOrder?.entry_items ||
                                                        []
                                                    }
                                                />
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                    <CardActions
                                        sx={{
                                            p: 2,
                                            pt: 0,
                                            flexWrap: "wrap",
                                            gap: 1,
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={handleCloseDetails}
                                            sx={{ fontWeight: 900 }}
                                        >
                                            Back to orders
                                        </Button>
                                        {!selectedOrder?.food_ready_time ? (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="large"
                                                startIcon={
                                                    <CheckCircleOutline />
                                                }
                                                onClick={() =>
                                                    handleMarkReady(
                                                        selectedOrder
                                                    )
                                                }
                                                disabled={
                                                    markReadyMutation.isLoading
                                                }
                                                sx={{ fontWeight: 900 }}
                                            >
                                                Mark ready
                                            </Button>
                                        ) : null}
                                    </CardActions>
                                </Card>
                            );
                        })()
                    ) : isLoading ? (
                        <Typography variant="body2" color="text.secondary">
                            Loading orders…
                        </Typography>
                    ) : pendingOrders.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No pending orders.
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "repeat(1, minmax(0, 1fr))",
                                    sm: "repeat(2, minmax(0, 1fr))",
                                    md: "repeat(3, minmax(0, 1fr))",
                                    lg: "repeat(4, minmax(0, 1fr))",
                                    xl: "repeat(5, minmax(0, 1fr))",
                                },
                                gap: 1.5,
                                alignContent: "start",
                            }}
                        >
                            {pendingOrders.map((order) => {
                                const itemsForCenter = productionCenterDoc
                                    ? order?.__filteredEntryItems || []
                                    : order?.entry_items || [];
                                const createdAt = getOrderCreatedAt(order);
                                const timerRefAt =
                                    lastFoodReadyAtByOrderName.get(
                                        order?.name
                                    ) || createdAt;
                                const totalQty =
                                    getItemsQtyTotal(itemsForCenter);
                                const waiterLabel = getWaiterLabel(
                                    order?.owner,
                                    usersByName
                                );
                                const late = isOrderLate({
                                    order,
                                    lastFoodReadyAt:
                                        lastFoodReadyAtByOrderName.get(
                                            order?.name
                                        ),
                                    thresholdMinutes,
                                    enabled: foodLateTimeEnabled,
                                });
                                return (
                                    <Card
                                        key={order?.name}
                                        elevation={0}
                                        sx={{
                                            border: "2px solid",
                                            borderColor: late
                                                ? "error.main"
                                                : "warning.main",
                                            bgcolor: late
                                                ? "error.50"
                                                : "warning.50",
                                            display: "flex",
                                            flexDirection: "column",
                                            minWidth: 0,
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                pb: 1,
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Stack spacing={1.25}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-start",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 900,
                                                                lineHeight: 1.15,
                                                            }}
                                                            noWrap
                                                        >
                                                            {order?.table_description ||
                                                                "Order"}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {order?.name}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        size="small"
                                                        color={
                                                            late
                                                                ? "error"
                                                                : "warning"
                                                        }
                                                        label={
                                                            late
                                                                ? "Late"
                                                                : "Pending"
                                                        }
                                                    />
                                                </Box>

                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    flexWrap="wrap"
                                                    useFlexGap
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={`Items: ${totalQty}`}
                                                    />
                                                    {timerRefAt ? (
                                                        <Chip
                                                            size="small"
                                                            label={fToNow(
                                                                timerRefAt
                                                            )}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            size="small"
                                                            label="Created: —"
                                                        />
                                                    )}
                                                    {waiterLabel ? (
                                                        <Chip
                                                            size="small"
                                                            label={waiterLabel}
                                                        />
                                                    ) : null}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                        <CardActions
                                            sx={{
                                                pt: 0,
                                                px: 2,
                                                pb: 2,
                                                gap: 1,
                                                mt: "auto",
                                            }}
                                        >
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                size="large"
                                                color="success"
                                                startIcon={
                                                    <CheckCircleOutline />
                                                }
                                                onClick={() =>
                                                    handleMarkReady(order)
                                                }
                                                disabled={
                                                    markReadyMutation.isLoading
                                                }
                                                sx={{ fontWeight: 900 }}
                                            >
                                                Mark ready
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                size="large"
                                                onClick={() =>
                                                    setDetailOrderName(
                                                        order?.name
                                                    )
                                                }
                                                sx={{ fontWeight: 900 }}
                                            >
                                                Details
                                            </Button>
                                        </CardActions>
                                    </Card>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );
}

export default KitchenDashboard;
